import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  formatDate,
  formatDealerStatus,
  NOTE_TYPE_LABELS,
  VERIFICATION_CHECKLIST_ITEMS,
} from "../../../constants/automapDealerOnboardingConstants";
import { fetchDealerTrustSummary } from "../../../services/automapTrustSafetyApi";
import { fetchFoundingDealerPanel } from "../../../services/automapFoundingDealerApi";
import { fetchDealerTrialDetail } from "../../../services/automapDealerTrialApi";
import {
  CONVERSION_READINESS_LABELS,
  CONVERSION_STAGE_LABELS,
  TRIAL_STATUS_LABELS,
} from "../../../constants/automapDealerTrialConstants";
import { READINESS_LABELS } from "../../../constants/automapFoundingDealerConstants";
import { trackDealerVerificationEvent } from "../../../utils/dealerVerificationDisplay";
import { statusBadgeClass } from "./dealerAdminShared";
import { AdminDealerTrialDetailContent } from "./AdminDealerTrialDetailPage";

export function AdminDealerOverviewPage() {
  const { dealer } = useOutletContext();
  const [trustSummary, setTrustSummary] = useState(null);
  const [foundingPanel, setFoundingPanel] = useState(null);
  const [trialPanel, setTrialPanel] = useState(null);
  const primaryContact = dealer.contacts?.find((c) => c.isPrimary) || dealer.contacts?.[0];
  const primaryLocation = dealer.locations?.find((l) => l.isPrimary) || dealer.locations?.[0];

  useEffect(() => {
    if (!dealer?.id) return;
    fetchDealerTrustSummary(dealer.id)
      .then(setTrustSummary)
      .catch(() => setTrustSummary(null));
    fetchFoundingDealerPanel(dealer.id)
      .then((data) => setFoundingPanel(data.panel))
      .catch(() => setFoundingPanel(null));
    fetchDealerTrialDetail(dealer.id)
      .then((data) => setTrialPanel(data.trial))
      .catch(() => setTrialPanel(null));
  }, [dealer?.id]);

  return (
    <section className="admin-detail-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
      <div>
        <h3>Información general</h3>
        <p>Estado: <span className={`admin-badge ${statusBadgeClass(dealer.status)}`}>{formatDealerStatus(dealer.status)}</span></p>
        <p>Verificación: <span className={`admin-badge ${statusBadgeClass(dealer.verificationStatus)}`}>{formatDealerStatus(dealer.verificationStatus)}</span></p>
        <p>Trial: <span className={`admin-badge ${statusBadgeClass(dealer.trialStatus)}`}>{formatDealerStatus(dealer.trialStatus)}</span> ({dealer.trialDaysRemaining ?? "—"} días)</p>
        {trialPanel ? (
          <p>
            Readiness: {trialPanel.readinessScore ?? "—"} · {CONVERSION_READINESS_LABELS[trialPanel.conversionReadiness] || trialPanel.conversionReadiness}
            {" · "}
            {CONVERSION_STAGE_LABELS[trialPanel.conversionStage] || trialPanel.conversionStage}
            {" · "}
            <Link to={`/admin/dealers/${dealer.id}/trial`}>Abrir trial</Link>
            {" · "}
            <Link to={`/admin/dealers/${dealer.id}/performance`}>Performance</Link>
          </p>
        ) : null}
        <p>Plan: {dealer.planType}</p>
        <p><Link to={`/admin/dealers/${dealer.id}/plan`}>Gestionar plan pagado</Link></p>
        <p>Perfil público: {dealer.publicProfileEnabled ? "Habilitado" : "Deshabilitado"}</p>
        <p>Razón social: {dealer.legalName || "—"}</p>
        <p>NIT: {dealer.taxId || "—"}</p>
        <p>WhatsApp: {dealer.mainWhatsapp || "—"}</p>
        <p>Email: {dealer.mainEmail || "—"}</p>
        <p>Web: {dealer.websiteUrl || "—"}</p>
        <p>Instagram: {dealer.instagramUrl || "—"}</p>
        <p>Financiación: {dealer.offersFinancing || "—"}</p>
        <p>Sales owner: {dealer.assignedSalesOwner || "—"}</p>
      </div>
      <div>
        <h3>Operación</h3>
        <p>Listings activos: {dealer.summary?.activeListings ?? 0}</p>
        <p>Leads este mes: {dealer.summary?.leadsThisMonth ?? 0}</p>
        <p>Leads calientes: {dealer.summary?.hotLeads ?? 0}</p>
        <p>Última actualización inventario: {formatDate(dealer.summary?.lastInventoryUpdate)}</p>
        <p>Última actividad lead: {formatDate(dealer.summary?.lastLeadActivity)}</p>
        <p>Contacto principal: {primaryContact?.name || "—"}</p>
        <p>Ubicación: {[primaryLocation?.city || dealer.city, primaryLocation?.address || dealer.address].filter(Boolean).join(" · ") || "—"}</p>
        <p><Link to={`/admin/dealers/${dealer.id}/performance`}>Ver rendimiento</Link></p>
        {(dealer.isFoundingDealer || dealer.planType === "founding_free" || foundingPanel?.pipeline) ? (
          <>
            <h4>Dealer fundador</h4>
            <p>Estado: {dealer.foundingDealerStatus || foundingPanel?.pipeline?.pipelineStage || "—"}</p>
            <p>Readiness: {foundingPanel?.onboarding?.readinessScore ?? dealer.readinessScore ?? "—"} — {READINESS_LABELS[foundingPanel?.onboarding?.readinessLabel] || dealer.onboardingStatus || "—"}</p>
            <p>Tareas abiertas: {foundingPanel?.openTasks ?? 0} · Blockers: {foundingPanel?.openBlockers ?? 0}</p>
            <p>
              <Link to={`/admin/dealers/${dealer.id}/onboarding`}>Onboarding</Link>
              {" · "}
              <Link to={`/admin/dealers/${dealer.id}/readiness`}>Readiness</Link>
              {" · "}
              <Link to={`/admin/inventory-quality/stale?dealerId=${dealer.id}`}>Calidad inventario</Link>
            </p>
          </>
        ) : null}
        <p><Link to={`/admin/listings?dealerId=${dealer.id}`}>Ver inventario admin</Link></p>
        <p><Link to={`/admin/leads?dealerId=${dealer.id}`}>Ver leads admin</Link></p>
        <h4>Confianza y seguridad</h4>
        {trustSummary ? (
          <>
            <p>Reportes activos: {trustSummary.reportCount ?? 0}</p>
            <p>Casos abiertos: {trustSummary.openCaseCount ?? 0}</p>
            {trustSummary.repeatIssue ? (
              <p><span className="admin-badge admin-badge--warning">Issues repetidos</span></p>
            ) : null}
            {trustSummary.cases?.some((c) => ["high", "critical"].includes(c.severity)) ? (
              <p><span className="admin-badge admin-badge--warning">Caso abierto de confianza de alta severidad</span></p>
            ) : null}
            <p>
              <Link to={`/admin/trust-safety/dealers`}>Cola dealers</Link>
              {" · "}
              <Link to={`/admin/trust-safety/cases?dealerId=${dealer.id}`}>Ver casos</Link>
            </p>
          </>
        ) : (
          <p className="muted">Sin reportes de confianza.</p>
        )}
        <h4>Notas recientes</h4>
        {(dealer.recentNotes || []).length ? (
          <ul>{dealer.recentNotes.map((n) => <li key={n.id}>{NOTE_TYPE_LABELS[n.noteType] || n.noteType}: {n.body.slice(0, 80)}</li>)}</ul>
        ) : <p className="muted">Sin notas.</p>}
        <h4>Actividad reciente</h4>
        {(dealer.recentActivity || []).length ? (
          <ul>{dealer.recentActivity.map((a) => <li key={a.id}>{a.title} — {formatDate(a.createdAt)}</li>)}</ul>
        ) : <p className="muted">Sin actividad registrada.</p>}
      </div>
    </section>
  );
}

export function AdminDealerContactsPage() {
  const { dealer, dealerId, canManage, reload } = useOutletContext();
  const [form, setForm] = useState({ name: "", role: "", whatsapp: "", email: "", isPrimary: false, receivesLeadNotifications: true });
  const [error, setError] = useState("");

  const save = async () => {
    try {
      const { createAdminDealerContact, trackDealerEvent } = await import("../../../services/automapDealerOnboardingApi");
      await createAdminDealerContact(dealerId, form);
      trackDealerEvent("admin_dealer_contact_added", { dealerId });
      setForm({ name: "", role: "", whatsapp: "", email: "", isPrimary: false, receivesLeadNotifications: true });
      setError("");
      await reload();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (contactId) => {
    if (!window.confirm("¿Eliminar contacto?")) return;
    const { deleteAdminDealerContact } = await import("../../../services/automapDealerOnboardingApi");
    await deleteAdminDealerContact(dealerId, contactId);
    await reload();
  };

  return (
    <section>
      <h3>Contactos</h3>
      {error ? <p className="muted">{error}</p> : null}
      <table className="admin-table">
        <thead><tr><th>Nombre</th><th>Rol</th><th>WhatsApp</th><th>Email</th><th>Principal</th><th>Leads</th><th /></tr></thead>
        <tbody>
          {(dealer.contacts || []).map((c) => (
            <tr key={c.id}>
              <td>{c.name}</td><td>{c.role || "—"}</td><td>{c.whatsapp || "—"}</td><td>{c.email || "—"}</td>
              <td>{c.isPrimary ? "Sí" : "—"}</td><td>{c.receivesLeadNotifications ? "Sí" : "No"}</td>
              <td>{canManage ? <button type="button" onClick={() => remove(c.id)}>Eliminar</button> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {canManage ? (
        <div className="admin-form-grid" style={{ marginTop: "1rem" }}>
          <input placeholder="Nombre *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input placeholder="Rol" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
          <input placeholder="WhatsApp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
          <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <label><input type="checkbox" checked={form.isPrimary} onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })} /> Principal</label>
          <label><input type="checkbox" checked={form.receivesLeadNotifications} onChange={(e) => setForm({ ...form, receivesLeadNotifications: e.target.checked })} /> Notificaciones leads</label>
          <button type="button" className="admin-btn admin-btn--primary" onClick={save}>Agregar contacto</button>
        </div>
      ) : null}
    </section>
  );
}

export function AdminDealerLocationsPage() {
  const { dealer, dealerId, canManage, reload } = useOutletContext();
  const [form, setForm] = useState({ name: "Sede", city: "", neighborhood: "", address: "", whatsapp: "", isPrimary: false, publicVisible: true });
  const [error, setError] = useState("");

  const save = async () => {
    try {
      const { createAdminDealerLocation, trackDealerEvent } = await import("../../../services/automapDealerOnboardingApi");
      await createAdminDealerLocation(dealerId, form);
      trackDealerEvent("admin_dealer_location_added", { dealerId });
      setForm({ name: "Sede", city: "", neighborhood: "", address: "", whatsapp: "", isPrimary: false, publicVisible: true });
      setError("");
      await reload();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (locationId) => {
    if (!window.confirm("¿Archivar ubicación?")) return;
    const { deleteAdminDealerLocation } = await import("../../../services/automapDealerOnboardingApi");
    await deleteAdminDealerLocation(dealerId, locationId);
    await reload();
  };

  return (
    <section>
      <h3>Ubicaciones</h3>
      {error ? <p className="muted">{error}</p> : null}
      <table className="admin-table">
        <thead><tr><th>Nombre</th><th>Ciudad</th><th>Dirección</th><th>Principal</th><th>Pública</th><th /></tr></thead>
        <tbody>
          {(dealer.locations || []).map((l) => (
            <tr key={l.id}>
              <td>{l.name}</td><td>{l.city}</td><td>{l.address || "—"}</td>
              <td>{l.isPrimary ? "Sí" : "—"}</td><td>{l.publicVisible !== false ? "Sí" : "No"}</td>
              <td>{canManage ? <button type="button" onClick={() => remove(l.id)}>Archivar</button> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {canManage ? (
        <div className="admin-form-grid" style={{ marginTop: "1rem" }}>
          <input placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input placeholder="Ciudad *" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <input placeholder="Barrio" value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} />
          <input placeholder="Dirección" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <label><input type="checkbox" checked={form.isPrimary} onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })} /> Principal</label>
          <label><input type="checkbox" checked={form.publicVisible} onChange={(e) => setForm({ ...form, publicVisible: e.target.checked })} /> Visible en perfil</label>
          <button type="button" className="admin-btn admin-btn--primary" onClick={save}>Agregar ubicación</button>
        </div>
      ) : null}
    </section>
  );
}

export function AdminDealerUsersPage() {
  const { dealerId, canManage, handleViewAsDealer, portalOpening } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", role: "viewer" });

  const load = async () => {
    const { fetchAdminDealerUsers } = await import("../../../services/automapDealerOnboardingApi");
    const data = await fetchAdminDealerUsers(dealerId);
    setUsers(data.users || []);
  };

  useEffect(() => { load(); }, [dealerId]);

  const add = async () => {
    const { createAdminDealerUser, trackDealerEvent } = await import("../../../services/automapDealerOnboardingApi");
    await createAdminDealerUser(dealerId, form);
    trackDealerEvent("admin_dealer_user_added", { dealerId });
    setForm({ name: "", email: "", whatsapp: "", role: "viewer" });
    await load();
  };

  const disable = async (userId) => {
    const { updateAdminDealerUser } = await import("../../../services/automapDealerOnboardingApi");
    await updateAdminDealerUser(dealerId, userId, { status: "disabled" });
    await load();
  };

  return (
    <section>
      <div className="admin-console__header-actions" style={{ marginBottom: "1rem" }}>
        <h3 style={{ margin: 0 }}>Usuarios del concesionario</h3>
        {canManage ? (
          <button
            type="button"
            className="admin-btn admin-btn--primary"
            disabled={portalOpening}
            onClick={() => handleViewAsDealer?.()}
          >
            {portalOpening ? "Abriendo portal…" : "View as dealer"}
          </button>
        ) : null}
      </div>
      <p className="muted">
        Create dealer login accounts or open the dealer portal as an admin to see exactly what the
        dealership sees after sign-in.
      </p>
      <table className="admin-table">
        <thead><tr><th>Nombre</th><th>Email</th><th>Rol</th><th>Estado</th><th>Acciones</th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td>{u.name}</td><td>{u.email || "—"}</td><td>{u.role}</td><td>{u.status}</td>
              <td>
                {canManage && u.status === "active" ? (
                  <button type="button" className="admin-btn admin-btn--ghost" disabled={portalOpening} onClick={() => handleViewAsDealer?.(u.id)}>
                    Open portal
                  </button>
                ) : null}
                {canManage && u.status !== "disabled" ? (
                  <button type="button" onClick={() => disable(u.id)}>Deshabilitar</button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canManage ? (
        <div className="admin-form-grid" style={{ marginTop: "1rem" }}>
          <input placeholder="Nombre *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input placeholder="WhatsApp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {["owner", "manager", "salesperson", "inventory_manager", "viewer"].map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <button type="button" className="admin-btn admin-btn--primary" onClick={add}>Agregar usuario</button>
        </div>
      ) : null}
    </section>
  );
}

export function AdminDealerVerificationPage() {
  const { dealer, dealerId, canManage, reload, runAction } = useOutletContext();
  const checklist = dealer.verificationChecklist || {};
  const [trustSummary, setTrustSummary] = useState(null);

  useEffect(() => {
    fetchDealerTrustSummary(dealerId)
      .then(setTrustSummary)
      .catch(() => setTrustSummary(null));
  }, [dealerId]);

  const toggle = async (key, value) => {
    const { updateAdminDealerVerificationChecklist } = await import("../../../services/automapDealerOnboardingApi");
    await updateAdminDealerVerificationChecklist(dealerId, { [key]: value });
    trackDealerVerificationEvent("admin_dealer_verification_checklist_updated", { dealerId, key, value });
    await reload();
  };

  const handleVerificationAction = async (action, extra = {}) => {
    await runAction(action, extra);
    trackDealerVerificationEvent("admin_dealer_verification_status_changed", { dealerId, action });
    await reload();
  };

  return (
    <section>
      <h3>Verificación</h3>
      <p>Estado: <span className={`admin-badge ${statusBadgeClass(dealer.verificationStatus)}`}>{formatDealerStatus(dealer.verificationStatus)}</span></p>
      {dealer.verificationStatus === "verified" && ["suspended", "rejected", "archived"].includes(dealer.status) ? (
        <p><span className="admin-badge admin-badge--warning">Badge público oculto por estado del concesionario</span></p>
      ) : null}
      {trustSummary?.cases?.some((c) => ["high", "critical"].includes(c.severity)) ? (
        <p><span className="admin-badge admin-badge--warning">Caso abierto de confianza de alta severidad. TODO: reglas automáticas de revisión.</span></p>
      ) : null}
      <ul className="dealer-checklist">
        {VERIFICATION_CHECKLIST_ITEMS.map((item) => (
          <li key={item.key}>
            <label>
              <input type="checkbox" disabled={!canManage} checked={Boolean(checklist[item.key])} onChange={(e) => toggle(item.key, e.target.checked)} />
              {" "}{item.label}{item.required ? " *" : ""}
            </label>
          </li>
        ))}
      </ul>
      {canManage ? (
        <div className="admin-console__header-actions">
          <button type="button" className="admin-btn" onClick={() => handleVerificationAction("mark_pending_verification")}>Marcar pendiente</button>
          <button type="button" className="admin-btn admin-btn--primary" onClick={() => handleVerificationAction("mark_verified", { checklist })} disabled={!dealer.verificationComplete}>Marcar verificado</button>
          <button type="button" className="admin-btn" onClick={() => handleVerificationAction("reject_verification")}>Rechazar</button>
          <button type="button" className="admin-btn" onClick={() => handleVerificationAction("suspend_verification")}>Suspender</button>
          <button type="button" className="admin-btn" onClick={() => handleVerificationAction("reopen_verification")}>Reabrir</button>
        </div>
      ) : null}
    </section>
  );
}

export function AdminDealerTrialPage() {
  const { dealerId } = useOutletContext();
  return <AdminDealerTrialDetailContent embedded dealerId={dealerId} />;
}

export function AdminDealerNotesPage() {
  const { dealerId, canManage } = useOutletContext();
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [noteType, setNoteType] = useState("general");

  const load = async () => {
    const { fetchAdminDealerNotes } = await import("../../../services/automapDealerOnboardingApi");
    const data = await fetchAdminDealerNotes(dealerId);
    setNotes(data.notes || []);
  };

  useEffect(() => { load(); }, [dealerId]);

  const add = async () => {
    const { createAdminDealerNote, trackDealerEvent } = await import("../../../services/automapDealerOnboardingApi");
    await createAdminDealerNote(dealerId, { body, noteType });
    trackDealerEvent("admin_dealer_note_added", { dealerId });
    setBody("");
    await load();
  };

  const archive = async (noteId) => {
    const { deleteAdminDealerNote } = await import("../../../services/automapDealerOnboardingApi");
    await deleteAdminDealerNote(dealerId, noteId);
    await load();
  };

  return (
    <section>
      <h3>Notas internas</h3>
      <ul>{notes.map((n) => (
        <li key={n.id} style={{ marginBottom: "0.75rem" }}>
          <strong>{NOTE_TYPE_LABELS[n.noteType] || n.noteType}</strong> · {formatDate(n.createdAt)}
          <p>{n.body}</p>
          {canManage ? <button type="button" onClick={() => archive(n.id)}>Archivar</button> : null}
        </li>
      ))}</ul>
      {canManage ? (
        <div className="admin-form-grid">
          <select value={noteType} onChange={(e) => setNoteType(e.target.value)}>
            {Object.entries(NOTE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Escribir nota…" />
          <button type="button" className="admin-btn admin-btn--primary" onClick={add}>Agregar nota</button>
        </div>
      ) : null}
    </section>
  );
}

export function AdminDealerActivityPage() {
  const { dealerId } = useOutletContext();
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    (async () => {
      const { fetchAdminDealerActivity } = await import("../../../services/automapDealerOnboardingApi");
      const data = await fetchAdminDealerActivity(dealerId);
      setActivity(data.activity || []);
    })();
  }, [dealerId]);

  return (
    <section>
      <h3>Historial de actividad</h3>
      {!activity.length ? <p className="muted">Sin eventos registrados.</p> : (
        <ul>
          {activity.map((item) => (
            <li key={item.id} style={{ marginBottom: "0.5rem" }}>
              <strong>{item.title}</strong>
              {item.description ? ` — ${item.description}` : ""}
              {item.previousValue && item.newValue ? ` (${item.previousValue} → ${item.newValue})` : ""}
              <div className="muted">{formatDate(item.createdAt)}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const LEAD_ROUTING_MODE_OPTIONS = [
  { value: "direct_to_dealer", label: "Directo al concesionario" },
  { value: "internal_review", label: "Revisión interna" },
  { value: "disabled", label: "Deshabilitado" },
];

const LEAD_NOTIFICATION_CHANNEL_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "internal_only", label: "Solo interno" },
  { value: "none", label: "Ninguno" },
];

const LEAD_CONTACT_AVAILABILITY_OPTIONS = [
  { value: "available", label: "Disponible" },
  { value: "limited", label: "Limitado" },
  { value: "unavailable", label: "No disponible" },
  { value: "unknown", label: "Desconocido" },
];

export function AdminDealerLeadPreferencesPage() {
  const { dealerId, canManage } = useOutletContext();
  const [preferences, setPreferences] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { fetchAdminDealerLeadPreferences } = await import("../../../services/automapDealerOnboardingApi");
      const data = await fetchAdminDealerLeadPreferences(dealerId);
      setPreferences(data.preferences);
      setForm({
        leadsEnabled: data.preferences.leadsEnabled !== false,
        leadRoutingMode: data.preferences.leadRoutingMode || "direct_to_dealer",
        leadNotificationEmail: data.preferences.leadNotificationEmail || "",
        leadNotificationChannel: data.preferences.leadNotificationChannel || "email",
        leadContactAvailability: data.preferences.leadContactAvailability || "unknown",
        leadNotificationPaused: Boolean(data.preferences.leadNotificationPaused),
        leadNotificationPausedReason: data.preferences.leadNotificationPausedReason || "",
        leadAutoReplyEnabled: Boolean(data.preferences.leadAutoReplyEnabled),
        leadMaxDailyNotifications: data.preferences.leadMaxDailyNotifications ?? "",
      });
      setError("");
    } catch (err) {
      setError(err.message || "No se pudieron cargar las preferencias.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [dealerId]);

  const save = async (event) => {
    event.preventDefault();
    if (!canManage) return;
    setSaving(true);
    setSuccess("");
    setError("");
    try {
      const { updateAdminDealerLeadPreferences } = await import("../../../services/automapDealerOnboardingApi");
      const payload = {
        ...form,
        leadNotificationEmail: form.leadNotificationEmail?.trim() || null,
        leadMaxDailyNotifications: form.leadMaxDailyNotifications === "" ? null : Number(form.leadMaxDailyNotifications),
      };
      const data = await updateAdminDealerLeadPreferences(dealerId, payload);
      setPreferences(data.preferences);
      setSuccess("Preferencias guardadas.");
    } catch (err) {
      setError(err.message || "No se pudieron guardar las preferencias.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="muted">Cargando preferencias de leads…</p>;
  if (!form) return <p className="muted">{error || "Preferencias no disponibles."}</p>;

  return (
    <section>
      <h3>Preferencias de leads</h3>
      <p className="muted">
        Configuración interna de enrutamiento y notificaciones. No se expone al perfil público ni a compradores.
      </p>
      {error ? <p className="muted">{error}</p> : null}
      {success ? <p>{success}</p> : null}
      <form className="admin-form-grid" onSubmit={save}>
        <label>
          <input
            type="checkbox"
            checked={form.leadsEnabled}
            disabled={!canManage}
            onChange={(e) => setForm({ ...form, leadsEnabled: e.target.checked })}
          />
          {" "}Leads habilitados para este concesionario
        </label>
        <label>
          Modo de enrutamiento
          <select
            value={form.leadRoutingMode}
            disabled={!canManage}
            onChange={(e) => setForm({ ...form, leadRoutingMode: e.target.value })}
          >
            {LEAD_ROUTING_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label>
          Email de notificación
          <input
            type="email"
            placeholder="opcional — usa contactos si está vacío"
            value={form.leadNotificationEmail}
            disabled={!canManage}
            onChange={(e) => setForm({ ...form, leadNotificationEmail: e.target.value })}
          />
        </label>
        <label>
          Canal de notificación
          <select
            value={form.leadNotificationChannel}
            disabled={!canManage}
            onChange={(e) => setForm({ ...form, leadNotificationChannel: e.target.value })}
          >
            {LEAD_NOTIFICATION_CHANNEL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label>
          Disponibilidad de contacto (público)
          <select
            value={form.leadContactAvailability}
            disabled={!canManage}
            onChange={(e) => setForm({ ...form, leadContactAvailability: e.target.value })}
          >
            {LEAD_CONTACT_AVAILABILITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label>
          <input
            type="checkbox"
            checked={form.leadNotificationPaused}
            disabled={!canManage}
            onChange={(e) => setForm({ ...form, leadNotificationPaused: e.target.checked })}
          />
          {" "}Pausar notificaciones al concesionario
        </label>
        <label>
          Motivo de pausa (interno)
          <textarea
            rows={2}
            value={form.leadNotificationPausedReason}
            disabled={!canManage}
            onChange={(e) => setForm({ ...form, leadNotificationPausedReason: e.target.value })}
          />
        </label>
        <label>
          Máx. notificaciones diarias (opcional)
          <input
            type="number"
            min="1"
            value={form.leadMaxDailyNotifications}
            disabled={!canManage}
            onChange={(e) => setForm({ ...form, leadMaxDailyNotifications: e.target.value })}
          />
        </label>
        {canManage ? (
          <button type="submit" className="admin-btn" disabled={saving}>
            {saving ? "Guardando…" : "Guardar preferencias"}
          </button>
        ) : null}
      </form>
      {preferences ? (
        <dl style={{ marginTop: "1.5rem" }}>
          <div><dt>Estado notificación</dt><dd>{preferences.leadNotificationStatus || "—"}</dd></div>
          <div><dt>Destino resuelto (enmascarado)</dt><dd>{preferences.resolvedDestinationMasked || "—"}</dd></div>
          <div><dt>Acepta leads públicos</dt><dd>{preferences.canAcceptPublicLeads ? "Sí" : "No"}</dd></div>
          <div><dt>Notificar concesionario</dt><dd>{preferences.shouldNotifyDealer ? "Sí" : "No"}</dd></div>
          {preferences.leadPreferenceUpdatedAt ? (
            <div><dt>Última actualización</dt><dd>{formatDate(preferences.leadPreferenceUpdatedAt)}</dd></div>
          ) : null}
        </dl>
      ) : null}
    </section>
  );
}
